<template>
  <BaseTemplate app-name="Monitoramento" page-title="">
    <template #content>
      <div class="min-h-screen bg-slate-100 px-3 py-4 transition-colors duration-300 dark:bg-gray-900 sm:px-4 lg:px-6">
        <div class="mx-auto max-w-[1920px] space-y-4">

          <!-- ====== CABEÇALHO ====== -->
          <header class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div class="min-w-0">
              <h1 class="sr-only">Operação de Tomógrafos</h1>
              <img :src="LogoDark" alt="Operação de Tomógrafos" class="h-8 w-auto max-w-full object-contain sm:h-10" />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ unidades.length }} unidades · {{ totalEquipamentos }} tomógrafos
                <span v-if="ultimaAtualizacao"> · atualizado às {{ horaUltimaAtualizacao }}</span>
                <span v-if="erroCarregamento" class="ml-1 font-medium text-red-600 dark:text-red-400">· falha na última atualização</span>
              </p>
            </div>

            <div class="flex items-center gap-2">
              <label class="relative flex-1 lg:w-72">
                <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input v-model.trim="busca" type="search" class="input pl-9" placeholder="Buscar unidade ou tomógrafo" aria-label="Buscar unidade ou tomógrafo" />
              </label>
              <button type="button" class="btn-secondary shrink-0" :disabled="isRefreshing" @click="atualizar" aria-label="Atualizar agora">
                <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': isRefreshing }" />
                <span class="hidden sm:inline">Atualizar</span>
              </button>
            </div>
          </header>

          <!-- ====== INDICADORES ====== -->
          <section class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6" aria-label="Indicadores">
            <button
              v-for="kpi in kpis"
              :key="kpi.key"
              type="button"
              class="card group relative flex flex-col justify-between overflow-hidden p-3 text-left transition sm:p-4"
              :class="[
                kpi.filtro ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : 'cursor-default',
                kpi.filtro && filtroStatus === kpi.filtro ? 'ring-2 ring-offset-2 ring-offset-slate-100 dark:ring-offset-gray-900 ' + kpi.ring : ''
              ]"
              :disabled="!kpi.filtro"
              :aria-pressed="kpi.filtro ? filtroStatus === kpi.filtro : undefined"
              @click="kpi.filtro && alternarFiltro(kpi.filtro)"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="rounded-lg p-2" :class="kpi.iconBg">
                  <component :is="kpi.icon" class="h-5 w-5" :class="kpi.iconColor" />
                </span>
                <span v-if="kpi.percentual !== null" class="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {{ kpi.percentual }}%
                </span>
              </div>
              <div class="mt-3">
                <p class="truncate font-bold leading-none text-gray-900 dark:text-white" :class="kpi.valorClass">{{ kpi.valor }}</p>
                <p class="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">{{ kpi.label }}</p>
              </div>
              <div v-if="kpi.percentual !== null" class="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div class="h-full rounded-full transition-all duration-500" :class="kpi.bar" :style="{ width: kpi.percentual + '%' }"></div>
              </div>
            </button>
          </section>

          <!-- ====== DISPONIBILIDADE GERAL ====== -->
          <section class="card p-3 sm:p-4" aria-label="Disponibilidade da rede">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Disponibilidade da rede</h2>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                <span class="text-lg font-bold" :class="disponibilidadeClasse">{{ disponibilidadeEquipamentos }}%</span>
                dos tomógrafos operando
              </p>
            </div>
            <div class="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" role="img" :aria-label="`${unidadesEmFuncionamento} unidades em pleno funcionamento, ${unidadesEmFuncionamentoParcial} parciais e ${unidadesNaoFuncionando} fora de operação`">
              <div class="bg-emerald-500 transition-all duration-500" :style="{ width: calcularPercentual(unidadesEmFuncionamento) + '%' }"></div>
              <div class="bg-amber-400 transition-all duration-500" :style="{ width: calcularPercentual(unidadesEmFuncionamentoParcial) + '%' }"></div>
              <div class="bg-red-500 transition-all duration-500" :style="{ width: calcularPercentual(unidadesNaoFuncionando) + '%' }"></div>
            </div>
            <ul class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <li v-for="(meta, chave) in STATUS_META" :key="chave" class="flex items-center gap-1.5">
                <span class="h-2.5 w-2.5 rounded-full" :class="meta.dot"></span>{{ meta.label }}
              </li>
            </ul>
          </section>

          <!-- ====== FILTROS ====== -->
          <div class="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar unidades">
            <button
              v-for="chip in chipsFiltro"
              :key="chip.valor ?? 'todas'"
              type="button"
              class="badge border px-3 py-1 transition"
              :class="filtroStatus === chip.valor
                ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
              @click="filtroStatus = chip.valor"
            >
              <span v-if="chip.dot" class="h-2 w-2 rounded-full" :class="chip.dot"></span>
              {{ chip.label }}
              <span class="rounded-full bg-black/10 px-1.5 text-[10px] dark:bg-white/10">{{ chip.total }}</span>
            </button>
            <button
              type="button"
              class="badge border px-3 py-1 transition"
              :class="apenasComEventos
                ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
              :aria-pressed="apenasComEventos"
              @click="apenasComEventos = !apenasComEventos"
            >
              <CalendarClock class="h-3.5 w-3.5" /> Com eventos em aberto
            </button>
            <p class="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {{ unidadesFiltradas.length }} de {{ unidades.length }} unidades
            </p>
          </div>

          <!-- ====== LISTA DE UNIDADES ====== -->
          <div v-if="isLoading" class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" aria-busy="true">
            <div v-for="n in 10" :key="n" class="card h-44 animate-pulse bg-gray-200/60 dark:bg-gray-700/40"></div>
          </div>

          <div v-else-if="erroCarregamento && !unidades.length" class="card flex flex-col items-center gap-3 p-10 text-center">
            <XCircle class="h-10 w-10 text-red-500" />
            <p class="font-medium text-gray-700 dark:text-gray-200">Não foi possível carregar as unidades.</p>
            <button type="button" class="btn-primary" @click="atualizar">Tentar novamente</button>
          </div>

          <div v-else-if="!unidadesFiltradas.length" class="card flex flex-col items-center gap-2 p-10 text-center">
            <Search class="h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p class="font-medium text-gray-700 dark:text-gray-200">Nenhuma unidade encontrada com os filtros atuais.</p>
            <button type="button" class="btn-ghost text-blue-600 dark:text-blue-400" @click="limparFiltros">Limpar filtros</button>
          </div>

          <TransitionGroup v-else name="list" tag="div" class="relative grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            <article
              v-for="unidade in unidadesFiltradas"
              :key="unidade.id"
              class="card relative flex flex-col overflow-hidden transition hover:shadow-md"
            >
              <!-- Faixa de status -->
              <div class="absolute inset-y-0 left-0 w-1.5" :class="statusMeta(unidade.status).bar" aria-hidden="true"></div>

              <header class="space-y-1.5 p-3 pl-4">
                <h2 class="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-white" :title="unidade.nome">
                  {{ unidade.nome }}
                </h2>
                <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <span class="badge" :class="statusMeta(unidade.status).badge">
                    <span class="h-2 w-2 rounded-full" :class="statusMeta(unidade.status).dot"></span>
                    {{ unidade.status_label || statusMeta(unidade.status).label }}
                  </span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-gray-700 dark:text-gray-200">{{ unidade.equipamentos_operacionais }}/{{ unidade.total_equipamentos }}</span>
                    operando
                  </span>
                </div>
              </header>

              <ul class="flex-1 space-y-1.5 px-3 pb-3 pl-4">
                <li
                  v-for="equipamento in unidade.equipamentos"
                  :key="equipamento.id"
                  class="rounded-lg bg-gray-50 p-2 dark:bg-gray-900/60"
                >
                  <div class="flex items-center justify-between gap-2">
                    <span class="truncate text-sm font-medium text-gray-800 dark:text-gray-100" :title="equipamento.nome">
                      {{ equipamento.nome }}
                    </span>
                    <span class="badge shrink-0" :class="equipamentoMeta(equipamento.status).badge">
                      {{ equipamentoMeta(equipamento.status).label }}
                    </span>
                  </div>
                  <div v-if="especificacoes(equipamento).length" class="mt-1 flex flex-wrap gap-1">
                    <span
                      v-for="spec in especificacoes(equipamento)"
                      :key="spec"
                      class="rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-500 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700"
                    >
                      {{ spec }}
                    </span>
                  </div>
                </li>
              </ul>

              <footer class="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 pl-4 dark:border-gray-700">
                <button
                  v-if="unidade.eventos && unidade.eventos.length"
                  type="button"
                  class="btn-ghost px-2 py-1 text-xs text-blue-600 dark:text-blue-400"
                  @click="abrirModal(unidade)"
                >
                  <CalendarClock class="h-4 w-4" />
                  {{ unidade.eventos.length }} {{ unidade.eventos.length === 1 ? 'evento em aberto' : 'eventos em aberto' }}
                </button>
                <span v-else class="text-xs text-gray-400 dark:text-gray-500">Sem eventos em aberto</span>
                <span v-if="custoUnidade(unidade) > 0" class="text-xs font-semibold text-amber-600 dark:text-amber-400" :title="'Custos de reparo em atendimento'">
                  {{ formatarOrcamento(custoUnidade(unidade)) }}
                </span>
              </footer>
            </article>
          </TransitionGroup>
        </div>
      </div>

      <!-- ====== MODAL DE EVENTOS (teleportado: o conteúdo do BaseTemplate cria um stacking context) ====== -->
      <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="modalAberto"
          class="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          :aria-label="`Eventos da unidade ${unidadeSelecionada?.nome}`"
          @click.self="fecharModal"
        >
          <div class="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl animate-fade-in-up dark:bg-gray-800 sm:max-w-2xl sm:rounded-2xl">
            <header class="flex items-center justify-between gap-3 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white sm:px-6">
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wide text-blue-100">Eventos em aberto</p>
                <h3 class="truncate text-base font-bold sm:text-lg">{{ unidadeSelecionada?.nome }}</h3>
              </div>
              <button type="button" class="rounded-full p-1.5 transition hover:bg-white/20" aria-label="Fechar" @click="fecharModal">
                <X class="h-5 w-5" />
              </button>
            </header>

            <div class="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
              <button
                v-for="evento in unidadeSelecionada?.eventos"
                :key="evento.id"
                type="button"
                class="card w-full p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md"
                @click="navegarParaEvento(evento.id)"
              >
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <h4 class="font-semibold text-gray-900 dark:text-gray-100">{{ evento.equipamento_nome }}</h4>
                  <span class="badge" :class="eventoMeta(evento.status).badge">{{ eventoMeta(evento.status).label }}</span>
                </div>
                <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">{{ evento.motivo }}</p>
                <p v-if="evento.descricao" class="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{{ evento.descricao }}</p>
                <dl class="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                  <div>
                    <dt class="text-gray-500 dark:text-gray-400">Início</dt>
                    <dd class="font-medium text-gray-800 dark:text-gray-200">{{ formatarData(evento.data_inicio) }}</dd>
                  </div>
                  <div>
                    <dt class="text-gray-500 dark:text-gray-400">{{ evento.status === 'pendente' ? 'Previsão' : 'Término' }}</dt>
                    <dd class="font-medium text-gray-800 dark:text-gray-200">{{ formatarDataFinal(evento.data_previsao) }}</dd>
                  </div>
                  <div v-if="parseFloat(evento.orcamento) > 0">
                    <dt class="text-gray-500 dark:text-gray-400">Orçamento</dt>
                    <dd class="font-medium text-gray-800 dark:text-gray-200">{{ formatarOrcamento(parseFloat(evento.orcamento)) }}</dd>
                  </div>
                </dl>
                <p class="mt-3 text-xs font-medium text-blue-600 dark:text-blue-400">Ver detalhes →</p>
              </button>
            </div>

            <footer class="flex justify-end border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-800 sm:px-6">
              <button type="button" class="btn-secondary" @click="fecharModal">Fechar</button>
            </footer>
          </div>
        </div>
      </Transition>
      </Teleport>
    </template>
  </BaseTemplate>
</template>

<script setup>
import BaseTemplate from '@/views/templates/BaseTemplate.vue';
import LogoDark from '@/assets/Logo_Op_Tomografos.png';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  CheckCircle2, AlertTriangle, XCircle, Activity, Wrench, Wallet,
  Search, RefreshCw, CalendarClock, X
} from 'lucide-vue-next';
import router from '@/router';
import apiClient from '@/services/authService';

// ---------------------------------------------------------------------------
// Metadados de status (unidade, equipamento e evento)
// ---------------------------------------------------------------------------
const STATUS_META = {
  em_funcionamento: {
    label: 'Pleno funcionamento',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  em_atendimento: {
    label: 'Funcionamento parcial',
    dot: 'bg-amber-400',
    bar: 'bg-amber-400',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  parado: {
    label: 'Fora de operação',
    dot: 'bg-red-500',
    bar: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  },
};
const STATUS_DESCONHECIDO = {
  label: 'Indefinido',
  dot: 'bg-gray-400',
  bar: 'bg-gray-400',
  badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};
// Ordem de urgência para a listagem: parado > parcial > pleno
const ORDEM_STATUS = { parado: 0, em_atendimento: 1, em_funcionamento: 2 };

const EQUIPAMENTO_META = {
  em_funcionamento: { label: 'Operando', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  em_atendimento: { label: 'Em atendimento', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  parado: { label: 'Parado', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

const EVENTO_META = {
  pendente: { label: 'Pendente', badge: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  em_atendimento: { label: 'Em atendimento', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  concluido: { label: 'Concluído', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  finalizado: { label: 'Finalizado', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
};

const statusMeta = (status) => STATUS_META[status] || STATUS_DESCONHECIDO;
const equipamentoMeta = (status) => EQUIPAMENTO_META[status] || { label: status || 'Indefinido', badge: STATUS_DESCONHECIDO.badge };
const eventoMeta = (status) => EVENTO_META[status] || { label: status || 'Outro', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' };

// ---------------------------------------------------------------------------
// Estado
// ---------------------------------------------------------------------------
const unidades = ref([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const erroCarregamento = ref(false);
const ultimaAtualizacao = ref(null);

const busca = ref('');
const filtroStatus = ref(null);        // null | em_funcionamento | em_atendimento | parado
const apenasComEventos = ref(false);

const modalAberto = ref(false);
const unidadeSelecionada = ref(null);

// ---------------------------------------------------------------------------
// Carregamento
// ---------------------------------------------------------------------------
const normalizarUnidade = (unidade) => {
  const equipamentos = unidade.equipamentos || [];
  const operacionais = equipamentos.filter(e => e.status === 'em_funcionamento').length;
  // O backend já envia o status derivado dos equipamentos; os campos abaixo só
  // preenchem o que faltar em respostas antigas.
  return {
    ...unidade,
    eventos: unidade.eventos || [],
    total_equipamentos: unidade.total_equipamentos ?? equipamentos.length,
    equipamentos_operacionais: unidade.equipamentos_operacionais ?? operacionais,
    equipamentos_inoperantes: unidade.equipamentos_inoperantes ?? (equipamentos.length - operacionais),
    status_label: unidade.status_label || statusMeta(unidade.status).label,
  };
};

async function fetchUnidades() {
  try {
    const response = await apiClient.get('/api/v1/enterprises/monitoring/enterprises/');
    unidades.value = response.data.map(normalizarUnidade);
    ultimaAtualizacao.value = new Date();
    erroCarregamento.value = false;
  } catch (error) {
    erroCarregamento.value = true;
    console.error('Erro ao carregar unidades:', error);
  } finally {
    isLoading.value = false;
  }
}

async function atualizar() {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    await fetchUnidades();
  } finally {
    isRefreshing.value = false;
  }
}

const horaUltimaAtualizacao = computed(() =>
  ultimaAtualizacao.value
    ? ultimaAtualizacao.value.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : ''
);

// ---------------------------------------------------------------------------
// Totais e indicadores
// ---------------------------------------------------------------------------
const contarUnidades = (status) => unidades.value.filter(u => u.status === status).length;
const unidadesEmFuncionamento = computed(() => contarUnidades('em_funcionamento'));
const unidadesEmFuncionamentoParcial = computed(() => contarUnidades('em_atendimento'));
const unidadesNaoFuncionando = computed(() => contarUnidades('parado'));
const totalUnidades = computed(() => unidades.value.length);

const totalEquipamentos = computed(() => unidades.value.reduce((t, u) => t + u.total_equipamentos, 0));
const equipamentosEmFuncionamento = computed(() => unidades.value.reduce((t, u) => t + u.equipamentos_operacionais, 0));
const equipamentosParados = computed(() => unidades.value.reduce((t, u) => t + u.equipamentos_inoperantes, 0));

const custoUnidade = (unidade) =>
  unidade.eventos
    .filter(evento => evento.status === 'em_atendimento')
    .reduce((subtotal, evento) => subtotal + (parseFloat(evento.orcamento) || 0), 0);

const somaOrcamentoEmAtendimento = computed(() => unidades.value.reduce((total, u) => total + custoUnidade(u), 0));

function calcularPercentual(valor) {
  if (totalUnidades.value === 0) return 0;
  return +((valor / totalUnidades.value) * 100).toFixed(1);
}
function calcularPercentualEquip(valor) {
  if (totalEquipamentos.value === 0) return 0;
  return +((valor / totalEquipamentos.value) * 100).toFixed(1);
}

const disponibilidadeEquipamentos = computed(() => calcularPercentualEquip(equipamentosEmFuncionamento.value));
const disponibilidadeClasse = computed(() => {
  const v = disponibilidadeEquipamentos.value;
  if (v >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (v >= 70) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
});

const formatarOrcamento = valor =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(valor || 0);

const kpis = computed(() => [
  {
    key: 'unidades-ok', filtro: 'em_funcionamento', icon: CheckCircle2,
    valor: unidadesEmFuncionamento.value, percentual: calcularPercentual(unidadesEmFuncionamento.value),
    label: 'Unidades em pleno funcionamento',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', iconColor: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500', ring: 'ring-emerald-500', valorClass: 'text-2xl sm:text-3xl',
  },
  {
    key: 'unidades-parcial', filtro: 'em_atendimento', icon: AlertTriangle,
    valor: unidadesEmFuncionamentoParcial.value, percentual: calcularPercentual(unidadesEmFuncionamentoParcial.value),
    label: 'Unidades em funcionamento parcial',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40', iconColor: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-400', ring: 'ring-amber-400', valorClass: 'text-2xl sm:text-3xl',
  },
  {
    key: 'unidades-parado', filtro: 'parado', icon: XCircle,
    valor: unidadesNaoFuncionando.value, percentual: calcularPercentual(unidadesNaoFuncionando.value),
    label: 'Unidades fora de operação',
    iconBg: 'bg-red-100 dark:bg-red-900/40', iconColor: 'text-red-600 dark:text-red-400',
    bar: 'bg-red-500', ring: 'ring-red-500', valorClass: 'text-2xl sm:text-3xl',
  },
  {
    key: 'tomografos-ok', filtro: null, icon: Activity,
    valor: equipamentosEmFuncionamento.value, percentual: calcularPercentualEquip(equipamentosEmFuncionamento.value),
    label: 'Tomógrafos operando',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', iconColor: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500', ring: '', valorClass: 'text-2xl sm:text-3xl',
  },
  {
    key: 'tomografos-parados', filtro: null, icon: Wrench,
    valor: equipamentosParados.value, percentual: calcularPercentualEquip(equipamentosParados.value),
    label: 'Tomógrafos inoperantes',
    iconBg: 'bg-red-100 dark:bg-red-900/40', iconColor: 'text-red-600 dark:text-red-400',
    bar: 'bg-red-500', ring: '', valorClass: 'text-2xl sm:text-3xl',
  },
  {
    key: 'custos', filtro: null, icon: Wallet,
    valor: formatarOrcamento(somaOrcamentoEmAtendimento.value), percentual: null,
    label: 'Custos de reparos em atendimento',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40', iconColor: 'text-blue-600 dark:text-blue-400',
    bar: '', ring: '', valorClass: 'text-lg sm:text-xl',
  },
]);

// ---------------------------------------------------------------------------
// Filtros e ordenação
// ---------------------------------------------------------------------------
const chipsFiltro = computed(() => [
  { valor: null, label: 'Todas', dot: null, total: totalUnidades.value },
  { valor: 'parado', label: STATUS_META.parado.label, dot: STATUS_META.parado.dot, total: unidadesNaoFuncionando.value },
  { valor: 'em_atendimento', label: STATUS_META.em_atendimento.label, dot: STATUS_META.em_atendimento.dot, total: unidadesEmFuncionamentoParcial.value },
  { valor: 'em_funcionamento', label: STATUS_META.em_funcionamento.label, dot: STATUS_META.em_funcionamento.dot, total: unidadesEmFuncionamento.value },
]);

const normalizarTexto = (texto) =>
  String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const unidadesFiltradas = computed(() => {
  const termo = normalizarTexto(busca.value);
  return unidades.value
    .filter(u => !filtroStatus.value || u.status === filtroStatus.value)
    .filter(u => !apenasComEventos.value || u.eventos.length > 0)
    .filter(u => !termo
      || normalizarTexto(u.nome).includes(termo)
      || u.equipamentos.some(e => normalizarTexto(e.nome).includes(termo) || normalizarTexto(e.modelo).includes(termo)))
    .sort((a, b) =>
      (ORDEM_STATUS[a.status] ?? 9) - (ORDEM_STATUS[b.status] ?? 9)
      || b.eventos.length - a.eventos.length
      || a.nome.localeCompare(b.nome, 'pt-BR'));
});

function alternarFiltro(status) {
  filtroStatus.value = filtroStatus.value === status ? null : status;
}
function limparFiltros() {
  filtroStatus.value = null;
  apenasComEventos.value = false;
  busca.value = '';
}

// Especificações do tomógrafo como chips (só as informadas)
function especificacoes(equipamento) {
  const specs = [];
  if (equipamento.modelo) specs.push(equipamento.modelo);
  if (equipamento.canais && Number(equipamento.canais) !== 0) specs.push(`${equipamento.canais} canais`);
  if (equipamento.gantry && Number(equipamento.gantry) !== 0) specs.push(`Ø ${equipamento.gantry} cm`);
  if (equipamento.largura_mesa && Number(equipamento.largura_mesa) !== 0) specs.push(`Mesa ${equipamento.largura_mesa} cm`);
  if (equipamento.peso) specs.push(`${equipamento.peso} kg`);
  return specs;
}

// ---------------------------------------------------------------------------
// Modal de eventos
// ---------------------------------------------------------------------------
function abrirModal(unidade) {
  unidadeSelecionada.value = unidade;
  modalAberto.value = true;
}
function fecharModal() {
  modalAberto.value = false;
  unidadeSelecionada.value = null;
}
function navegarParaEvento(eventoId) {
  router.push(`/eventos/${eventoId}`);
}
function tratarTecla(evento) {
  if (evento.key === 'Escape' && modalAberto.value) fecharModal();
}

// Datas chegam como ISO; exibe só a data no fuso local
function formatarData(data) {
  if (!data) return 'Não informado';
  const date = new Date(data);
  const ajustada = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return ajustada.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatarDataFinal(data) {
  return data ? formatarData(data) : 'Sem previsão';
}

// ---------------------------------------------------------------------------
// Ciclo de vida: atualização automática a cada minuto (só com a aba visível)
// ---------------------------------------------------------------------------
let intervalId;

onMounted(() => {
  fetchUnidades();
  intervalId = setInterval(() => {
    if (document.hidden) return;
    fetchUnidades();
  }, 60000);
  window.addEventListener('keydown', tratarTecla);
});

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
  window.removeEventListener('keydown', tratarTecla);
});
</script>
